/**
 * HelpPage — brief in-app landing page for help resources.
 *
 * The actual user guide PDF and demo video are hosted in SharePoint / MS
 * Stream (so the content team can update them without a code deploy, and
 * Stream provides captioning and other accessibility features). This page
 * just provides a lightweight intro and links out to those resources.
 *
 * It also exposes the same Request Support action as the user menu, so a
 * user who drilled into the help page doesn't have to go back to the menu
 * to file a ticket.
 */

import { HiBookOpen, HiVideoCamera, HiTicket, HiArrowRight } from 'react-icons/hi';
import { useAuth } from '../contexts/AuthContext';
import {
  APP_SETTING_KEYS,
  buildSupportTicketUrl,
  useAppSettings,
} from '../contexts/AppSettingsContext';

const roleDisplay = (role: string): string => {
  const map: Record<string, string> = {
    superAdmin: 'Super Administrator',
    organizationAdmin: 'Organization Admin',
    viewer: 'Viewer',
  };
  return map[role] || role;
};

export const HelpPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { get, isLoading, error } = useAppSettings();

  const userGuideUrl = get(APP_SETTING_KEYS.helpUserGuideUrl);
  const demoVideoUrl = get(APP_SETTING_KEYS.helpDemoVideoUrl);
  const supportUrlBase = get(APP_SETTING_KEYS.supportTicketUrlBase);

  const openExternal = (url: string) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleRequestSupport = () => {
    if (!supportUrlBase || !currentUser) return;
    const url = buildSupportTicketUrl({
      urlBase: supportUrlBase,
      subjectPrefix: get(APP_SETTING_KEYS.supportSubjectPrefix, '[AIU Directory]'),
      subjectTemplate: get(
        APP_SETTING_KEYS.supportSubjectTemplate,
        '{prefix} User Inquiry ({userName})'
      ),
      descriptionTemplate: get(
        APP_SETTING_KEYS.supportDescriptionTemplate,
        'Submitted from AIU Directory by {userName} ({userEmail}).\n\n---\n\n'
      ),
      userName: currentUser.name,
      userEmail: currentUser.email,
      userRole: roleDisplay(currentUser.role),
      orgName: '',
      pageUrl: window.location.href,
    });
    openExternal(url);
  };

  return (
    <div className="container mx-auto p-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Help &amp; Support</h1>
        <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
          Resources for using the AIU Directory.
        </p>
      </div>

      {/* Brief intro */}
      <section className="mb-8 p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <h2 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
          About this application
        </h2>
        <div className="space-y-3 text-gray-700 dark:text-white">
          <p>
            The AIU Directory is the authoritative, real-time source for contact information
            across Allegheny County&apos;s school districts, career and technical centers, and
            jointures. It replaces the legacy Word document–based directory with a living,
            user-editable platform.
          </p>
          <p>
            Organization administrators can keep their own organization&apos;s contacts,
            administrators, and board members up to date. All authenticated users can browse
            and search the directory. If you run into trouble or have a question, the
            resources below will get you pointed in the right direction.
          </p>
        </div>
      </section>

      {isLoading && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading help resources…</p>
      )}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">
          Couldn&apos;t load help resources: {error}
        </p>
      )}

      {/* Resource cards */}
      <div className="grid gap-6 md:grid-cols-3">
        {userGuideUrl && (
          <button
            onClick={() => openExternal(userGuideUrl)}
            className="group text-left p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-600 transition-all"
          >
            <div className="flex items-center justify-center w-12 h-12 mb-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <HiBookOpen className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold mb-1 text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              User Guide
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Written walkthrough of the directory, edit workflows, and common tasks.
            </p>
            <span className="mt-3 inline-flex items-center text-xs text-blue-600 dark:text-blue-400">
              Open in SharePoint <HiArrowRight className="ml-1" />
            </span>
          </button>
        )}

        {demoVideoUrl && (
          <button
            onClick={() => openExternal(demoVideoUrl)}
            className="group text-left p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-600 transition-all"
          >
            <div className="flex items-center justify-center w-12 h-12 mb-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <HiVideoCamera className="w-7 h-7 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold mb-1 text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
              Video Walkthrough
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Short demo of the directory, hosted in Microsoft Stream with captioning.
            </p>
            <span className="mt-3 inline-flex items-center text-xs text-purple-600 dark:text-purple-400">
              Open in Stream <HiArrowRight className="ml-1" />
            </span>
          </button>
        )}

        {supportUrlBase && (
          <button
            onClick={handleRequestSupport}
            className="group text-left p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md hover:shadow-lg hover:border-green-300 dark:hover:border-green-600 transition-all"
          >
            <div className="flex items-center justify-center w-12 h-12 mb-3 bg-green-100 dark:bg-green-900 rounded-lg">
              <HiTicket className="w-7 h-7 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold mb-1 text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
              Request Support
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Open a pre-filled service desk ticket for questions or technical issues.
            </p>
            <span className="mt-3 inline-flex items-center text-xs text-green-600 dark:text-green-400">
              New ticket <HiArrowRight className="ml-1" />
            </span>
          </button>
        )}
      </div>

      <p className="mt-8 text-xs text-gray-500 dark:text-gray-400">
        Tickets that look like technical issues are handled by the application team; questions
        about directory content or process (who to add, how often to update, etc.) are handled
        by the content team.
      </p>
    </div>
  );
};
